import { IMenuGroup, IMenuItem, IMenuSelected } from "@components/navigation";
import { IGetValueArgs, TInfoValue } from "@components/smart/FieldInfo";
import { UserPermissionCodes } from "@odata/CompanyPermissionEnums";
import { EntitySetName } from "@odata/GeneratedEntityTypes";
import {
    AccountingCode,
    CompanyPermissionCode,
    DocumentBusinessPartnerTypeCode,
    DocumentTypeCode,
    GeneralPermissionCode
} from "@odata/GeneratedEnums";
import { BatchRequest, IBatchResult } from "@odata/OData";
import { ODataQueryResult } from "@odata/ODataParser";
import { isFreeSubscription, isNotYetPayedForSubscription } from "@pages/admin/subscriptions/Subscriptions.utils";
import { hasEverBeenOrWillBeOssCompany } from "@pages/documents/Oss.global.utils"; // origName: menu+icons/svg/menu icons all_MZDY_rozvrhy pracovni doby.svg
import { ProformaType } from "@pages/documents/proformaInvoices/ProformaInvoice.global.utils";
import { shouldUseGrant } from "@pages/grant/Grant.utils";
import { getInboxFilterByType, InboxFilter } from "@pages/inbox/Inbox.utils";
import {
    getCompanyAccountingCode,
    hasEverBeenVatRegisteredCompany,
    hasPayrollEnabled,
    isDemoTenant,
} from "@utils/CompanyUtils";
import { isPayrollEnabledOnCurrentCompany, isPayrollEnabledOnTenantLevel } from "@utils/definition.utils";
import { isBasicDraftFilter } from "@utils/DraftUtils";
import { getValue, isDefined, isEmptyArray } from "@utils/general";
import i18next from "i18next";
import { cloneDeep } from "lodash";

import { IAppContext } from "./contexts/appContext/AppContext.types";
import { IPermissionContext } from "./contexts/permissionContext/PermissionContext";
import {
    ADVANCE_AND_WITHHOLDING_TAX_ROUTE,
    BENEFIT_OVERVIEW_ROUTE,
    DEDUCTION_OVERVIEW_ROUTE,
    HEALTH_INSURANCE_CONTRIBUTIONS_OVERVIEW_ROUTE,
    ROUTE_ABSENCE_CATEGORIES,
    ROUTE_ACCOUNT_ANALYSIS,
    ROUTE_ACCOUNT_ASSIGNMENT,
    ROUTE_ACCOUNTING_JOURNAL,
    ROUTE_ACCOUNTING_SETTINGS,
    ROUTE_ACTIONS,
    ROUTE_AGING_AP,
    ROUTE_AGING_AR,
    ROUTE_ANNUAL_TAX_SETTLEMENT,
    ROUTE_ANNUAL_TAX_SETTLEMENT_REQUEST,
    ROUTE_ASSET_ANALYSIS,
    ROUTE_ASSETS,
    ROUTE_ATTENDANCE,
    ROUTE_AUDIT,
    ROUTE_BACKGROUND_JOB,
    ROUTE_BALANCE_SHEET,
    ROUTE_BANK_ACCOUNTS,
    ROUTE_BANK_STATEMENTS,
    ROUTE_BANK_TRANSACTIONS,
    ROUTE_BENEFITS,
    ROUTE_BUSINESS_PARTNER,
    ROUTE_CASH_BOXES,
    ROUTE_CASH_RECEIPTS,
    ROUTE_CATEGORIES,
    ROUTE_CBA_ENTRY_LEDGER,
    ROUTE_CBA_EXPENSES_AND_REVENUE,
    ROUTE_CHARTS_OF_ACCOUNTS,
    ROUTE_CHARTS_OF_ACCOUNTS_TEMPLATES,
    ROUTE_CHATS,
    ROUTE_COMPANY_ROLES,
    ROUTE_COMPANY_SETTING,
    ROUTE_CORRECTIVE_INVOICE_ISSUED,
    ROUTE_CORRECTIVE_INVOICE_RECEIVED,
    ROUTE_CURRENCY_SETTINGS,
    ROUTE_CUSTOMER_PORTAL_PAYMENT,
    ROUTE_DATA_BOX,
    ROUTE_DATA_EXPORT,
    ROUTE_DATA_IMPORT,
    ROUTE_DEDUCTIONS,
    ROUTE_DELETE_LOG,
    ROUTE_DOCUMENT_ITEM_TEMPLATES,
    ROUTE_DOCUMENT_JOURNAL,
    ROUTE_DOCUMENTS,
    ROUTE_ELECTRONIC_SUBMISSION,
    ROUTE_EMPLOYEE_PAYSLIP,
    ROUTE_EMPLOYEES,
    ROUTE_EMPLOYMENT,
    ROUTE_EXCHANGE_RATES,
    ROUTE_EXPENSES_AND_REVENUE,
    ROUTE_EXTRA_SALARIES,
    ROUTE_FISCAL_YEAR,
    ROUTE_FISCAL_YEAR_CLOSE,
    ROUTE_FIXED_ASSET,
    ROUTE_GENERAL_LEDGER,
    ROUTE_GOVERNMENT_INSTITUTION,
    ROUTE_GRANT_JOURNAL,
    ROUTE_HEALTH_INSURANCE_COMPANIES,
    ROUTE_HOME,
    ROUTE_INBOX,
    ROUTE_INBOX_CUSTOMER_APPROVAL,
    ROUTE_INBOX_SEND_TO_ACCOUNTANT,
    ROUTE_INBOX_SETTINGS,
    ROUTE_INCOME_AND_EXPENDITURE,
    ROUTE_INCOME_STATEMENT,
    ROUTE_INITIAL_ACCOUNT_BALANCES,
    ROUTE_INTEGRATIONS,
    ROUTE_INTERNAL_DOCUMENT,
    ROUTE_INVOICE_ISSUED,
    ROUTE_INVOICE_RECEIVED,
    ROUTE_LABEL_HIERARCHIES,
    ROUTE_LOAN_ACCOUNTS,
    ROUTE_LOAN_STATEMENTS,
    ROUTE_LOAN_TRANSACTIONS,
    ROUTE_MENU_COMMON_SETTINGS,
    ROUTE_MENU_SUBSCRIPTION,
    ROUTE_MENU_USERS,
    ROUTE_MESSAGES,
    ROUTE_MINOR_ASSET,
    ROUTE_NONPROFIT_GRANT,
    ROUTE_NUMBER_RANGES,
    ROUTE_OTHER_LIABILITY,
    ROUTE_OTHER_RECEIVABLE,
    ROUTE_PAYMENT_JOURNAL,
    ROUTE_PAYMENTS,
    ROUTE_PAYROLL,
    ROUTE_PAYROLL_OVERVIEW,
    ROUTE_PAYROLL_SETTING,
    ROUTE_PAYROLL_YEAR_OPERATIONS,
    ROUTE_PAYSLIP,
    ROUTE_PAYSLIP_OVERVIEW,
    ROUTE_PROFORMA_INVOICE_ISSUED,
    ROUTE_PROFORMA_INVOICE_RECEIVED,
    ROUTE_RECEIVABLE_ADJUSTMENTS,
    ROUTE_RECURRING_TASKS,
    ROUTE_REPORTING,
    ROUTE_RETIREMENT_SAVINGS_COMPANIES,
    ROUTE_SALARY_ADVANCES,
    ROUTE_SALARY_COMPONENTS,
    ROUTE_SALDO,
    ROUTE_SOCIAL_EVENTS,
    ROUTE_SOCIAL_REQUESTS,
    ROUTE_SUBMISSIONS,
    ROUTE_SUBSCRIPTIONS_EXTENSIONS,
    ROUTE_SUBSCRIPTIONS_INVOICING,
    ROUTE_SUBSCRIPTIONS_PAYMENTS,
    ROUTE_SUBSCRIPTIONS_PLAN,
    ROUTE_SYSTEM_ROLES,
    ROUTE_TAXPAYER_DECLARATIONS,
    ROUTE_UNORGANIZED_ASSET,
    ROUTE_USERS,
    ROUTE_VAT,
    ROUTE_VAT_CONTROL_STATEMENT_REPORT,
    ROUTE_VAT_OSS_STATEMENT_REPORT,
    ROUTE_VAT_STATEMENT_REPORT,
    ROUTE_VAT_VIES_STATEMENT_REPORT,
    ROUTE_WORKING_PATTERNS,
    SOCIAL_SECURITY_CONTRIBUTIONS_OVERVIEW_ROUTE,
    TAX_CREDITS_AND_ALLOWANCES_OVERVIEW_ROUTE,
    VACATION_AND_SICK_DAYS_OVERVIEW_ROUTE
} from "./routes";
import accountAnalysisSvg from "./svg/menu/accountAnalysis.svg"; // origName: menu+icons/svg/menu icons all_REPORTY_analyza uctu.svg
import accountAssignmentSvg from "./svg/menu/accountAssignment.svg"; // origName: menu+icons/svg/menu icons all_SPOLECNA NASTAVENI_predpisy zauctovani.svg
import accountingJournalSvg from "./svg/menu/accountingJournal.svg"; // origName: menu+icons/svg/menu icons all_REPORTY_ucetni denik.svg
import advanceAndWithholdingTaxSvg from "./svg/menu/advanceAndWithholdingTax.svg"; // origName: menu+icons/svg/menu icons all_REPORTY MZDY_Prehled zalohove a srazkove dane.svg
import annualTaxSettlementSvg from "./svg/menu/annualTaxSettlement.svg"; // origName: menu+icons/svg/menu icons all_MZDY_RZD.svg
import annualTaxSettlementRequestsSvg from "./svg/menu/annualTaxSettlementRequests.svg"; // origName: menu+icons/svg/menu icons all_MZDY_Žádost o RZD.svg
import assetAnalysisSvg from "./svg/menu/assetAnalysis.svg"; // origName: menu+icons/svg/menu icons all_REPORTY_prehled majetku.svg
import attendanceSvg from "./svg/menu/attendance.svg"; // origName: menu+icons/svg/menu icons all_MZDY_dochazka a abscence.svg
import auditSvg from "./svg/menu/audit.svg"; // origName: menu+icons/svg/menu icons all_AUDIT_smazane doklady.svg
import backgroundJobSvg from "./svg/menu/backgroundJob.svg"; // origName: menu+icons/svg/menu icons all_AUDIT_LOG uloh.svg
import balanceSvg from "./svg/menu/balance.svg"; // origName: menu+icons/svg/menu icons all_REPORTY_rozvaha.svg
import bankStatementsSvg from "./svg/menu/bankStatements.svg"; // origName: menu+icons/svg/menu icons all_UHRADY_bankovni vypisy.svg
import bankTransactionsSvg from "./svg/menu/bankTransactions.svg"; // origName: menu+icons/svg/menu icons all_UHRADY_bankovni pohyby.svg
import benefitOverviewSvg from "./svg/menu/benefitOverview.svg"; // origName: menu+icons/svg/menu icons all_REPORTY MZDY_Prehled benefitu
import benefitsSvg from "./svg/menu/benefits.svg"; // origName: menu+icons/svg/menu icons all_MZDY_příspěvky.svg
import cashReceiptsSvg from "./svg/menu/cashReceipts.svg"; // origName: menu+icons/svg/menu icons all_UHRADY_pokladni doklady.svg
import cbaEntryLedgerSvg from "./svg/menu/cbaEntryLedger.svg"; // origName: menu+icons/svg/menu icons all_REPORTY–DANOVKA_evidencni denik.svg
import cbaIncomeExpenseSvg from "./svg/menu/cbaIncomeExpense.svg"; // origName: menu+icons/svg/menu icons all_REPORTY-DANOVKA_prehled prijmu a vydaju.svg
import coaTemplatesSvg from "./svg/menu/coaTemplates.svg"; // origName: menu+icons/svg/menu icons all_SPOLECNA NASTAVENI_sablony uct rozvrhu.svg
import commitmentsSvg from "./svg/menu/commitments.svg"; // origName: menu+icons/svg/menu icons all_REPORTY_stavy zavazku.svg
import companyRolesSvg from "./svg/menu/companyRoles.svg"; // origName: menu+icons/svg/menu icons all_UZIVATELE_agendove role.svg
import correctiveInvoicesIssuedSvg from "./svg/menu/correctiveInvoicesIssued.svg"; // origName: menu+icons/svg/menu icons all_DOKLADY_opravne doklady vydane.svg
import correctiveInvoicesReceivedSvg from "./svg/menu/correctiveInvoicesReceived.svg"; // origName: menu+icons/svg/menu icons all_DOKLADY_opravne doklday prijate.svg
import deductionOverviewSvg from "./svg/menu/deductionOverview.svg"; // origName: menu+icons/svg/menu icons all_REPORTY MZDY_Prehled srazek.svg
import deductionsSvg from "./svg/menu/deductions.svg"; // origName: menu+icons/svg/menu icons all_MZDY_srazky ze mzdy.svg
import documentJournalSvg from "./svg/menu/documentJournal.svg"; // origName: menu+icons/svg/menu icons all_REPORTY_dokladovy denik.svg
import electronicSubmissionSvg from "./svg/menu/electronicSubmission.svg"; // origName: menu+icons/svg/menu icons all_CINNOSTI_elektronicka podani.svg
import employeesSvg from "./svg/menu/employees.svg"; // origName: menu+icons/svg/menu icons all_MZDY_zamestnanci.svg
import employmentSvg from "./svg/menu/employment.svg"; // origName: menu+icons/svg/menu icons all_MZDY_pracovni poměry.svg
import exchangeRatesSvg from "./svg/menu/exchangeRates.svg"; // origName: menu+icons/svg/menu icons all_REPORTY_kurzovni listek.svg
import expensesAndRevenue from './svg/menu/expensesAndRevenue.svg'; //origName: menu icons all_REPORTY_naklady a vynosy.svg
import extraSalariesSvg from "./svg/menu/extraSalaries.svg"; // origName: menu+icons/svg/menu icons all_MZDY_mzdove priplatky.svg
import fiscalYearCloseSvg from "./svg/menu/fiscalYearClose.svg"; // origName: menu+icons/svg/menu icons all_CINNOSTI_uzaverka.svg
import fixedAssetSvg from "./svg/menu/fixedAsset.svg"; // origName: menu+icons/svg/menu icons all_MAJETEK_dlouhodoby majetek.svg
import generalLedgerSvg from "./svg/menu/generalLedger.svg"; // origName: menu+icons/svg/menu icons all_REPORTY_hlavni kniha.svg
import generalRolesSvg from "./svg/menu/generalRoles.svg"; // origName: menu+icons/svg/menu icons all_UZIVATELE_systemove role.svg
import grantsSvg from "./svg/menu/grant.svg"; // origName: standard+icons/menu+icons/svg/menu icons all_CINNOSTI_dotace.svg
import grantJournalSvg from "./svg/menu/grantJournal.svg"; // origName: menu icons all_REPORTY_dotacni denik.svg
import healthInsuranceContributionsOverviewSvg from "./svg/menu/healthInsuranceContributionsOverview.svg"; // origName: menu+icons/svg/menu icons all_REPORTY MZDY_Prehled zdravotniho pojisteni.svg
import inboxSvg from "./svg/menu/inbox.svg"; // origName: menu+icons/svg/menu icons all_DOKLADAY_inbox.svg
import incomeStatementSvg from "./svg/menu/incomeStatement.svg"; // origName: menu+icons/svg/menu icons all_REPORTY_vysledovka.svg
import internalDocumentsSvg from "./svg/menu/internalDocuments.svg"; // origName: menu+icons/svg/menu icons all_DOKLADY_interni doklady.svg
import invoicesIssuedSvg from "./svg/menu/invoicesIssued.svg"; // origName: menu+icons/svg/menu icons all_DOKLADY_faktury vydane.svg
import invoicesReceivedSvg from "./svg/menu/invoicesReceived.svg"; // origName: menu+icons/svg/menu icons all_DOKLADY_faktury prijate.svg
import itemTemplatesSvg from "./svg/menu/itemTemplates.svg"; // origName: menu+icons/svg/menu icons all_SPOLECNA NASTAVENI_ciselnik polozek.svg
import loanStatementsSvg from "./svg/menu/loanStatements.svg"; // origName: menu+icons/svg/menu icons all_UHRADY_uverove vypisy.svg
import loanTransactionSvg from "./svg/menu/loanTransactions.svg"; // origName: menu+icons/svg/menu icons all_UHRADY_uverove pohyby.svg
import messagesSvg from "./svg/menu/messages.svg"; // origName: menu+icons/svg/menu icons all_AUDIT_odeslane zpravy.svg
import minorAssetSvg from "./svg/menu/minorAsset.svg"; // origName: menu+icons/svg/menu icons all_MAJETEK_drobný majetek.svg
import openBalanceSvg from "./svg/menu/openBalance.svg"; // origName: menu+icons/svg/menu icons all_REPORTY_stavy pohledavek.svg
import otherLiabilitySvg from "./svg/menu/otherLiability.svg"; // origName: menu+icons/svg/menu icons all_DOKLADY_ostatni zavazky.svg
import otherReceivablesSvg from "./svg/menu/otherReceivables.svg"; // origName: menu+icons/svg/menu icons all_DOKLADY_ostatni pohledavky.svg
import paymentJournalSvg from "./svg/menu/paymentJournal.svg"; // origName: menu+icons/svg/menu icons all_REPORTY_denik uhrad.svg
import payrollOverviewSvg from "./svg/menu/payrollOverview.svg"; // origName: menu+icons/svg/menu icons all_REPORTY MZDY_Prehled vyplacenych mezd
import payslipSvg from "./svg/menu/payslip.svg"; // origName: menu+icons/svg/menu icons all_MZDY_vypocet mezd
import proformaInvoicesIssuedSvg from "./svg/menu/proformaInvoicesIssued.svg"; // origName: menu+icons/svg/menu icons all_DOKLADY_zalohove faktury vydane.svg
import proformaInvoicesReceivedSvg from "./svg/menu/proformaInvoicesReceived.svg"; // origName: menu+icons/svg/menu icons all_DOKLADY_zalohove faktury přijate.svg
import prSubmissionsSvg from "./svg/menu/prSubmissions.svg"; // origName: menu+icons/svg/menu icons all_MZDY_Fronta podání.svg
import prYearlyOperationsSvg from "./svg/menu/prYearlyOperations.svg"; // origName: menu+icons/svg/menu icons all_MZDY_rocni operace.svg
import receivableAdjustmentsMenuSvg from "./svg/menu/receivableAdjustmentsMenu.svg"; // origName: menu+icons/svg/menu icons all_CINNOSTI_opravne polozky k pohledavkam.svg
import retirementSavingsSvg from "./svg/menu/retirementsSavings.svg"; // origName: icons/menu icons/svg/menu icons all_SPOLECNA NASTAVENI_sporeni na stari.svg
import salaryAdvancesSvg from "./svg/menu/salaryAdvances.svg"; // origName: menu+icons/svg/menu icons all_MZDY_zalohy na mzdy.svg
import salaryComponentsSvg from "./svg/menu/salaryComponents.svg"; // origName: menu+icons/svg/menu icons all_MZDY_slozky mzdy.svg
import saldoSvg from "./svg/menu/saldo.svg"; // origName: menu+icons/svg/menu icons all_REPORTY_saldo.svg
import socialContributionOverviewSvg from "./svg/menu/socialContributionOverview.svg"; // origName: menu+icons/svg/menu icons all_REPORTY MZDY_Prehled socialniho pojisteni.svg
import socialEventsSvg from "./svg/menu/socialEvents.svg"; // origName: menu+icons/svg/menu icons all_MZDY_socialni udalosti.svg
import subscriptionExtensionsSvg from "./svg/menu/subscriptionExtensions.svg"; // origName: menu+icons/svg/menu icons all_PREDPLATNE_doplnkove moduly.svg
import subscriptionInvoicingSvg from "./svg/menu/subscriptionInvoicing.svg"; // origName: menu+icons/svg/menu icons all_PREDPLATNE_fakturacni udaje.svg
import subscriptionPaymentsSvg from "./svg/menu/subscriptionPayments.svg"; // origName: menu+icons/svg/menu icons all_PREDPLATNE_platba kartou.svg
import subscriptionPlanSvg from "./svg/menu/subscriptionPlan.svg"; // origName: menu+icons/svg/menu icons all_PREDPLATNE_prehled.svg
import taxCreditsAndTaxAllowancesOverviewSvg from "./svg/menu/taxCreditsAndTaxAllowancesOverview.svg"; // origName: menu+icons/svg/menu icons all_REPORTY MZDY_Prehled danovych slev a odpoctu.svg
import taxPayerDeclarationSvg from "./svg/menu/taxPayerDeclaration.svg"; // origName: menu+icons/svg/menu icons all_MZDY_prohlášení poplatníka.svg
import unsortedAssetSvg from "./svg/menu/unsortedAsset.svg"; // origName: menu+icons/svg/menu icons all_MAJETEK_vytvareni majetku.svg
import usersSvg from "./svg/menu/users.svg"; // origName: menu+icons/svg/menu icons all_UZIVATELE_nastaveni uzivatelu.svg
import vacationAndSickDaysOverviewSvg from "./svg/menu/vacationAndSickDaysOverview.svg"; // origName: menu+icons/svg/menu icons all_REPORTY MZDY_Prehled cerpani dovolene.svg
import vatControlStatementReportSvg from "./svg/menu/vatControlStatementReport.svg"; // origName: menu+icons/svg/menu icons all_REPORTY_kontrolni hlaseni.svg
import vatOssReportSvg from "./svg/menu/vatOssReport.svg"; // origName: menu+icons/svg/menu icons all_REPORTY_OSS hlaseni.svg
import vatRulesSvg from "./svg/menu/vatRules.svg"; // origName: menu+icons/svg/menu icons all_SPOLECNA NASTAVENI_pravidla DPH.svg
import vatStatementReportSvg from "./svg/menu/vatStetementReport.svg"; // origName: menu+icons/svg/menu icons all_REPORTY_priznani k DPH.svg
import vatViesReportSvg from "./svg/menu/vatViesStatementReport.svg"; // origName: menu+icons/svg/menu icons all_REPORTY_souhrnne hlaseni.svg
import workingPatternsSvg from "./svg/menu/workingPatterns.svg"; // origName: menu+icons/svg/menu icons all_MZDY_rozvrhy pracovni doby.svg

export interface IMenuDefItem extends IMenuItem {
    permission?: (CompanyPermissionCode | GeneralPermissionCode) | (CompanyPermissionCode | GeneralPermissionCode)[];
    countGetter?: (batch: BatchRequest) => Promise<number>;
    // used to:
    // - disable menu item based on some condition
    // - automatically redirects user to NotFound page if the menu item is disabled when the user navigates directly to the page
    isDisabledFn?: (context: IAppContext) => IDisabledMenuItem;
    accountingTypes?: AccountingCode[];
    // used to:
    // - hide menu item based on some condition
    // - automatically redirects user to NotFound page if the menu item is not visible when the user navigates directly to the page
    isVisible?: TInfoValue<boolean>;
}

interface IDisabledMenuItem {
    reason: string;
}

export interface IMenuDefGroup extends IMenuGroup {
    items: IMenuDefItem[];
}

const isBankStuffDisabled = (context: IAppContext): IDisabledMenuItem => {
    const accounts = context.getCompanyBankAccounts();
    if (isEmptyArray(accounts)) {
        return {
            reason: i18next.t("Common:Menu.NoBankAccount")
        };
    }
    return null;
};

const hasLoanAccount = (context: IAppContext): IDisabledMenuItem => {
    const accounts = context.getLoanAccounts();
    if (isEmptyArray(accounts)) {
        return {
            reason: i18next.t("Common:Menu.NoLoanAccount")
        };
    }
    return null;
};


export const SettingsDefinition: IMenuDefGroup[] = [
    {
        key: "Settings.GeneralSettings",
        title: "Settings.GeneralSettings",
        items: [
            {
                key: "CompanySetting",
                title: "Settings.CompanySetting",
                url: ROUTE_COMPANY_SETTING,
                permission: UserPermissionCodes.MasterData
            },
            {
                key: "BusinessPartners",
                title: "Settings.BusinessPartners",
                url: ROUTE_BUSINESS_PARTNER,
                permission: UserPermissionCodes.MasterData
            },
            {
                key: "BankAccounts",
                title: "Settings.BankAccounts",
                permission: UserPermissionCodes.MasterData,
                url: ROUTE_BANK_ACCOUNTS
            },
            {
                key: "LoanAccounts",
                title: "Settings.LoanAccounts",
                permission: UserPermissionCodes.MasterData,
                url: ROUTE_LOAN_ACCOUNTS
            },
            {
                key: "CashBoxes",
                title: "Settings.CashBoxes",
                url: ROUTE_CASH_BOXES,
                permission: UserPermissionCodes.MasterData
            },
            {
                key: "InboxSettings",
                title: "Settings.InboxSettings",
                url: ROUTE_INBOX_SETTINGS,
                permission: UserPermissionCodes.MasterData
            },
            {
                key: "DataBox",
                title: "Settings.DataBox",
                url: ROUTE_DATA_BOX,
                permission: UserPermissionCodes.MasterData
            },
            {
                key: "RecurringTasks",
                title: "Settings.RecurringTasks",
                url: ROUTE_RECURRING_TASKS,
                permission: UserPermissionCodes.RecurringTask
            },
            {
                key: "DataImport",
                title: "Settings.DataImport",
                url: ROUTE_DATA_IMPORT,
                permission: UserPermissionCodes.CanImport
            },
            {
                key: "DataExport",
                title: "Settings.DataExport",
                url: ROUTE_DATA_EXPORT,
                permission: GeneralPermissionCode.CanExport
            },
            {
                key: "Integrations",
                title: "Settings.Integrations",
                url: ROUTE_INTEGRATIONS,
                permission: UserPermissionCodes.MasterData
            }
        ]
    },
    {
        title: "Settings.Accounting",
        key: "Settings.Accounting",
        items: [
            {
                key: "Categories",
                title: "Settings.Categories",
                url: ROUTE_CATEGORIES,
                permission: UserPermissionCodes.MasterData,
                accountingTypes: [AccountingCode.CashBasisAccounting]
            },
            {
                key: "InitialBalances",
                title: "Settings.InitialBalances",
                url: ROUTE_INITIAL_ACCOUNT_BALANCES,
                permission: UserPermissionCodes.MasterData,
                accountingTypes: [AccountingCode.AccountingForBusiness, AccountingCode.AccountingForNonprofitOrganizations]
            },
            {
                key: "FiscalYear",
                title: "Settings.FiscalYear",
                url: ROUTE_FISCAL_YEAR,
                permission: UserPermissionCodes.MasterData,
                accountingTypes: [AccountingCode.AccountingForBusiness, AccountingCode.AccountingForNonprofitOrganizations]
            },
            {
                key: "ChartsOfAccounts",
                title: "Settings.ChartsOfAccounts",
                url: ROUTE_CHARTS_OF_ACCOUNTS,
                permission: UserPermissionCodes.MasterData,
                accountingTypes: [AccountingCode.AccountingForBusiness, AccountingCode.AccountingForNonprofitOrganizations]
            },
            {
                key: "NumberRanges",
                title: "Settings.NumberRanges",
                url: ROUTE_NUMBER_RANGES,
                permission: UserPermissionCodes.MasterData
            },
            {
                key: "Labels",
                title: "Settings.Labels",
                url: ROUTE_LABEL_HIERARCHIES,
                permission: UserPermissionCodes.MasterData
            },
            {
                key: "CurrencySettings",
                title: "Settings.CurrencySettings",
                url: ROUTE_CURRENCY_SETTINGS,
                permission: UserPermissionCodes.MasterData
            }
        ]
    },
    {
        key: "Settings.PayrollSettings",
        title: "Settings.PayrollSettings",
        items: [
            {
                key: "GeneralPayrollSetting",
                title: "Menu.GeneralPayrollSetting",
                url: ROUTE_PAYROLL_SETTING,
                permission: UserPermissionCodes.PrPayrollSetting
            },
            {
                key: "HealthInsuranceCompanies",
                title: "Menu.HealthInsuranceCompanies",
                url: ROUTE_HEALTH_INSURANCE_COMPANIES,
                permission: UserPermissionCodes.PrPayrollSetting
            },
            {
                key: "GovernmentInstitution",
                title: "Menu.GovernmentInstitution",
                url: ROUTE_GOVERNMENT_INSTITUTION,
                permission: UserPermissionCodes.PrPayrollSetting
            },
            {
                key: "AbsenceCategories",
                title: "Menu.AbsenceCategories",
                url: ROUTE_ABSENCE_CATEGORIES,
                permission: UserPermissionCodes.PrPayrollSetting
            },
            {
                key: "AccountingSettings",
                title: "Menu.AccountingSettings",
                url: ROUTE_ACCOUNTING_SETTINGS,
                permission: UserPermissionCodes.PrPayrollSetting
            }
        ],
        isVisible: isPayrollEnabledOnCurrentCompany
    }
];

const getDraftCountFilter = (documentType: DocumentTypeCode) => {
    return async (batch: BatchRequest): Promise<number> => {
        const query = batch.getEntitySetWrapper(EntitySetName.DocumentDrafts).query()
            .filter(`${isBasicDraftFilter} AND DocumentTypeCode eq '${documentType}'`).count().top(0);
        const res = (await batch.awaitRequest(query.getId())) as IBatchResult<ODataQueryResult>;

        return res.body._metadata?.count ?? 0;
    };
};

export const getMainMenuDefinition = (context: IAppContext): IMenuDefGroup[] => {
    return [
        {
            key: "Documents",
            title: "Menu.Documents",
            url: ROUTE_DOCUMENTS,
            items: [
                {
                    key: "InvoicesReceived",
                    iconPath: invoicesReceivedSvg,
                    title: "Menu.InvoicesReceived",
                    url: ROUTE_INVOICE_RECEIVED,
                    permission: UserPermissionCodes.InvoicesReceived,
                    countGetter: getDraftCountFilter(DocumentTypeCode.InvoiceReceived)
                },
                {
                    key: "InvoicesIssued",
                    iconPath: invoicesIssuedSvg,
                    title: "Menu.InvoicesIssued",
                    url: ROUTE_INVOICE_ISSUED,
                    permission: UserPermissionCodes.InvoicesIssued,
                    countGetter: getDraftCountFilter(DocumentTypeCode.InvoiceIssued)
                },
                {
                    key: "ProformaInvoicesReceived",
                    iconPath: proformaInvoicesReceivedSvg,
                    title: (): string => {
                        return hasEverBeenVatRegisteredCompany(context) ? "Menu.ProformaInvoicesReceived" : "Menu.ProformaInvoicesReceived_WithoutVat";
                    },
                    url: ROUTE_PROFORMA_INVOICE_RECEIVED,
                    permission: UserPermissionCodes.InvoicesReceived,
                    countGetter: getDraftCountFilter(DocumentTypeCode.ProformaInvoiceReceived)
                },
                {
                    key: "ProformaInvoicesIssued",
                    iconPath: proformaInvoicesIssuedSvg,
                    title: (): string => {
                        return hasEverBeenVatRegisteredCompany(context) ? "Menu.ProformaInvoicesIssued" : "Menu.ProformaInvoicesIssued_WithoutVat";
                    },
                    url: ROUTE_PROFORMA_INVOICE_ISSUED,
                    permission: UserPermissionCodes.InvoicesIssued,
                    countGetter: getDraftCountFilter(DocumentTypeCode.ProformaInvoiceIssued)
                },
                {
                    key: "OtherLiability",
                    iconPath: otherLiabilitySvg,
                    title: "Menu.OtherLiability",
                    url: ROUTE_OTHER_LIABILITY,
                    permission: UserPermissionCodes.InvoicesReceived,
                    countGetter: getDraftCountFilter(DocumentTypeCode.OtherLiability)
                },
                {
                    key: "OtherReceivables",
                    iconPath: otherReceivablesSvg,
                    title: "Menu.OtherReceivables",
                    url: ROUTE_OTHER_RECEIVABLE,
                    permission: UserPermissionCodes.InvoicesIssued,
                    countGetter: getDraftCountFilter(DocumentTypeCode.OtherReceivable)
                },
                {
                    key: "CorrectiveInvoicesReceived",
                    iconPath: correctiveInvoicesReceivedSvg,
                    title: "Menu.CorrectiveInvoicesReceived",
                    url: ROUTE_CORRECTIVE_INVOICE_RECEIVED,
                    permission: UserPermissionCodes.InternalDocuments,
                    countGetter: getDraftCountFilter(DocumentTypeCode.CorrectiveInvoiceReceived)
                },
                {
                    key: "CorrectiveInvoicesIssued",
                    iconPath: correctiveInvoicesIssuedSvg,
                    title: "Menu.CorrectiveInvoicesIssued",
                    url: ROUTE_CORRECTIVE_INVOICE_ISSUED,
                    permission: UserPermissionCodes.InternalDocuments,
                    countGetter: getDraftCountFilter(DocumentTypeCode.CorrectiveInvoiceIssued)
                },
                {
                    key: "InternalDocuments",
                    iconPath: internalDocumentsSvg,
                    title: "Menu.InternalDocuments",
                    url: ROUTE_INTERNAL_DOCUMENT,
                    permission: UserPermissionCodes.InternalDocuments,
                    countGetter: getDraftCountFilter(DocumentTypeCode.InternalDocument)
                },
                {
                    key: "Inbox",
                    iconPath: inboxSvg,
                    title: "Menu.Inbox",
                    permission: UserPermissionCodes.InBox,
                    url: ROUTE_INBOX,
                    countGetter: async (batch: BatchRequest): Promise<number> => {
                        const query = batch.getEntitySetWrapper(EntitySetName.InboxFiles).query()
                            .filter(getInboxFilterByType(InboxFilter.Unsorted)).count().top(0);
                        const res = (await batch.awaitRequest(query.getId())) as IBatchResult<ODataQueryResult>;

                        return res.body._metadata.count;
                    }
                }
            ]
        },
        {
            key: "Payments",
            title: "Menu.Payments",
            url: ROUTE_PAYMENTS,
            items: [
                {
                    key: "BankTransactions",
                    iconPath: bankTransactionsSvg,
                    title: "Menu.BankTransactions",
                    permission: UserPermissionCodes.Bank,
                    url: ROUTE_BANK_TRANSACTIONS,
                    isDisabledFn: isBankStuffDisabled
                },
                {
                    key: "BankStatements",
                    iconPath: bankStatementsSvg,
                    title: "Menu.BankStatements",
                    permission: UserPermissionCodes.Bank,
                    url: ROUTE_BANK_STATEMENTS,
                    isDisabledFn: isBankStuffDisabled
                }, {
                    key: "LoanTransactions",
                    iconPath: loanTransactionSvg,
                    title: "Menu.LoanTransactions",
                    permission: UserPermissionCodes.Bank,
                    url: ROUTE_LOAN_TRANSACTIONS,
                    isDisabledFn: hasLoanAccount
                }, {
                    key: "LoanStatements",
                    iconPath: loanStatementsSvg,
                    title: "Menu.LoanStatements",
                    permission: UserPermissionCodes.Bank,
                    url: ROUTE_LOAN_STATEMENTS,
                    isDisabledFn: hasLoanAccount

                }, {
                    key: "CashReceipts",
                    iconPath: cashReceiptsSvg,
                    title: "Menu.CashReceipts",
                    permission: UserPermissionCodes.CashBox,
                    url: ROUTE_CASH_RECEIPTS,
                    isDisabledFn: (context: IAppContext): IDisabledMenuItem => {
                        const cashBoxes = context.getCashBoxes();
                        if (isEmptyArray(cashBoxes)) {
                            return {
                                reason: i18next.t("Common:Menu.NoCashBbox")
                            };
                        }
                        return null;
                    }
                }
            ]
        },
        {
            key: "Assets",
            title: "Menu.Assets",
            url: ROUTE_ASSETS,
            items: [
                {
                    key: "UnorganizedAsset",
                    iconPath: unsortedAssetSvg,
                    title: "Menu.UnorganizedAsset",
                    permission: UserPermissionCodes.Asset,
                    url: ROUTE_UNORGANIZED_ASSET
                },
                {
                    key: "FixedAsset",
                    iconPath: fixedAssetSvg,
                    title: "Menu.FixedAssets",
                    permission: UserPermissionCodes.Asset,
                    url: ROUTE_FIXED_ASSET
                },
                {
                    key: "MinorAsset",
                    iconPath: minorAssetSvg,
                    title: "Menu.MinorAsset",
                    permission: UserPermissionCodes.Asset,
                    url: ROUTE_MINOR_ASSET
                }
            ]
        },
        {
            key: "Actions",
            title: "Menu.Actions",
            url: ROUTE_ACTIONS,
            items: [
                {
                    key: "ElectronicSubmission",
                    iconPath: electronicSubmissionSvg,
                    title: "Menu.ElectronicSubmissions",
                    permission: UserPermissionCodes.VatStatement,
                    url: ROUTE_ELECTRONIC_SUBMISSION,
                    isVisible: (args: IGetValueArgs): boolean => {
                        return hasEverBeenVatRegisteredCompany(args.context);
                    }
                },
                {
                    key: "ReceivableAdjustmentsMenu",
                    iconPath: receivableAdjustmentsMenuSvg,
                    title: "Menu.ReceivableAdjustments",
                    permission: UserPermissionCodes.ReceivableAdjustments,
                    url: ROUTE_RECEIVABLE_ADJUSTMENTS,
                    accountingTypes: [AccountingCode.AccountingForBusiness, AccountingCode.AccountingForNonprofitOrganizations]
                },
                {
                    key: "Grant",
                    iconPath: grantsSvg,
                    title: "Menu.NonProfitGrant",
                    url: ROUTE_NONPROFIT_GRANT,
                    isVisible: (args: IGetValueArgs): boolean => shouldUseGrant(args.context)
                },
                {
                    key: "FiscalYearClose",
                    iconPath: fiscalYearCloseSvg,
                    title: "Menu.FiscalYearClose",
                    permission: UserPermissionCodes.FiscalYearClose,
                    url: ROUTE_FISCAL_YEAR_CLOSE
                }
            ]
        },
        {
            key: "A7",
            title: "Menu.Reporting",
            url: ROUTE_REPORTING,
            items: [
                {
                    key: "CbaIncomeExpense",
                    iconPath: cbaIncomeExpenseSvg,
                    title: "Menu.CbaIncomeExpense",
                    url: ROUTE_INCOME_AND_EXPENDITURE,
                    permission: UserPermissionCodes.Reports,
                    accountingTypes: [AccountingCode.CashBasisAccounting]
                },
                {
                    key: "CbaEntryLedger",
                    iconPath: cbaEntryLedgerSvg,
                    title: "Menu.CbaEntryLedger",
                    url: ROUTE_CBA_ENTRY_LEDGER,
                    permission: UserPermissionCodes.Reports,
                    accountingTypes: [AccountingCode.CashBasisAccounting]
                },
                {
                    key: "GeneralLedger",
                    iconPath: generalLedgerSvg,
                    title: "Menu.GeneralLedger",
                    url: ROUTE_GENERAL_LEDGER,
                    permission: UserPermissionCodes.Reports,
                    accountingTypes: [AccountingCode.AccountingForBusiness, AccountingCode.AccountingForNonprofitOrganizations]
                },
                {
                    key: "AccountingJournal",
                    iconPath: accountingJournalSvg,
                    title: "Menu.Journal",
                    url: ROUTE_ACCOUNTING_JOURNAL,
                    permission: UserPermissionCodes.Reports,
                    accountingTypes: [AccountingCode.AccountingForBusiness, AccountingCode.AccountingForNonprofitOrganizations]
                },
                {
                    key: "AccountAnalysis",
                    iconPath: accountAnalysisSvg,
                    title: "Menu.AccountAnalysis",
                    url: ROUTE_ACCOUNT_ANALYSIS,
                    permission: UserPermissionCodes.Reports,
                    accountingTypes: [AccountingCode.AccountingForBusiness, AccountingCode.AccountingForNonprofitOrganizations]
                },
                {
                    key: "ExpensesAndRevenue",
                    iconPath: expensesAndRevenue,
                    title: "Menu.ExpensesAndRevenue",
                    url: ROUTE_EXPENSES_AND_REVENUE,
                    permission: UserPermissionCodes.Reports,
                    accountingTypes: [AccountingCode.AccountingForBusiness, AccountingCode.AccountingForNonprofitOrganizations]
                },

                {
                    key: "CbaExpensesAndRevenue",
                    iconPath: expensesAndRevenue,
                    title: "Menu.ExpensesAndRevenue",
                    url: ROUTE_CBA_EXPENSES_AND_REVENUE,
                    permission: UserPermissionCodes.Reports,
                    accountingTypes: [AccountingCode.CashBasisAccounting]
                },
                {
                    key: "AssetAnalysis",
                    iconPath: assetAnalysisSvg,
                    title: "Menu.AssetAnalysis",
                    url: ROUTE_ASSET_ANALYSIS,
                    permission: UserPermissionCodes.Reports
                },
                {
                    key: "DocumentJournal",
                    iconPath: documentJournalSvg,
                    title: "Menu.DocumentJournal",
                    url: ROUTE_DOCUMENT_JOURNAL,
                    permission: UserPermissionCodes.Reports
                },
                {
                    key: "VatStatementReport",
                    title: "Menu.VatStatementReport",
                    iconPath: vatStatementReportSvg,
                    url: ROUTE_VAT_STATEMENT_REPORT,
                    permission: UserPermissionCodes.Reports,
                    isVisible: (args: IGetValueArgs): boolean => {
                        return hasEverBeenVatRegisteredCompany(args.context);
                    }
                },
                {
                    key: "VatControlStatementReport",
                    title: "Menu.VatControlStatementReport",
                    iconPath: vatControlStatementReportSvg,
                    url: ROUTE_VAT_CONTROL_STATEMENT_REPORT,
                    permission: UserPermissionCodes.Reports,
                    isVisible: (args: IGetValueArgs): boolean => {
                        return hasEverBeenVatRegisteredCompany(args.context, true);
                    }
                },
                {
                    key: "VatVIESStatementReport",
                    title: "Menu.VatVIESStatementReport",
                    iconPath: vatViesReportSvg,
                    url: ROUTE_VAT_VIES_STATEMENT_REPORT,
                    permission: UserPermissionCodes.Reports,
                    isVisible: (args: IGetValueArgs): boolean => {
                        return hasEverBeenVatRegisteredCompany(args.context);
                    }
                },
                {
                    key: "VatOssReport",
                    title: "Menu.VatOssStatementReport",
                    iconPath: vatOssReportSvg,
                    url: ROUTE_VAT_OSS_STATEMENT_REPORT,
                    permission: UserPermissionCodes.Reports,
                    isVisible: (args: IGetValueArgs): boolean => {
                        return hasEverBeenVatRegisteredCompany(args.context) && hasEverBeenOrWillBeOssCompany(args.context);
                    }
                },
                {
                    key: "PaymentJournal",
                    iconPath: paymentJournalSvg,
                    title: "Menu.PaymentJournal",
                    url: ROUTE_PAYMENT_JOURNAL,
                    permission: UserPermissionCodes.Reports,
                    accountingTypes: [AccountingCode.AccountingForBusiness, AccountingCode.AccountingForNonprofitOrganizations]
                },
                {
                    key: "Saldo",
                    iconPath: saldoSvg,
                    title: "Menu.Saldo",
                    url: ROUTE_SALDO,
                    permission: UserPermissionCodes.Reports,
                    accountingTypes: [AccountingCode.AccountingForBusiness, AccountingCode.AccountingForNonprofitOrganizations]
                },
                {
                    key: "IncomeStatement",
                    iconPath: incomeStatementSvg,
                    title: "Menu.IncomeStatement",
                    url: ROUTE_INCOME_STATEMENT,
                    permission: UserPermissionCodes.Reports,
                    accountingTypes: [AccountingCode.AccountingForBusiness, AccountingCode.AccountingForNonprofitOrganizations]
                },
                {
                    key: "Balance",
                    iconPath: balanceSvg,
                    title: "Menu.Balance",
                    url: ROUTE_BALANCE_SHEET,
                    permission: UserPermissionCodes.Reports,
                    accountingTypes: [AccountingCode.AccountingForBusiness, AccountingCode.AccountingForNonprofitOrganizations]
                },
                {
                    key: "Commitments",
                    iconPath: commitmentsSvg,
                    title: "Menu.Commitments",
                    url: ROUTE_AGING_AP,
                    permission: UserPermissionCodes.Reports
                },
                {
                    key: "OpenBalance",
                    iconPath: openBalanceSvg,
                    title: "Menu.OpenBalance",
                    url: ROUTE_AGING_AR,
                    permission: UserPermissionCodes.Reports
                },
                {
                    key: "ExchangeRates",
                    iconPath: exchangeRatesSvg,
                    title: "Settings.ExchangeRates",
                    url: ROUTE_EXCHANGE_RATES,
                    permission: UserPermissionCodes.Reports
                },
                {
                    key: "GrantJournal",
                    iconPath: grantJournalSvg,
                    title: "Menu.GrantJournal",
                    url: ROUTE_GRANT_JOURNAL,
                    permission: UserPermissionCodes.Reports,
                    isVisible: (args) => shouldUseGrant(args.context),
                    accountingTypes: [AccountingCode.AccountingForBusiness, AccountingCode.AccountingForNonprofitOrganizations]
                },
                {
                    key: "PayrollOverview",
                    iconPath: payrollOverviewSvg,
                    title: "Menu.PayrollOverview",
                    url: ROUTE_PAYROLL_OVERVIEW,
                    permission: [UserPermissionCodes.Reports, UserPermissionCodes.PrPayroll],
                    isVisible: isPayrollEnabledOnCurrentCompany
                },
                {
                    key: "BenefitOverview",
                    iconPath: benefitOverviewSvg,
                    title: "Menu.BenefitOverview",
                    url: BENEFIT_OVERVIEW_ROUTE,
                    permission: [UserPermissionCodes.Reports, UserPermissionCodes.PrPayroll],
                    isVisible: isPayrollEnabledOnCurrentCompany
                },
                {
                    key: "DeductionOverview",
                    iconPath: deductionOverviewSvg,
                    title: "Menu.DeductionOverview",
                    url: DEDUCTION_OVERVIEW_ROUTE,
                    permission: [UserPermissionCodes.Reports, UserPermissionCodes.PrPayroll],
                    isVisible: isPayrollEnabledOnCurrentCompany
                },
                {
                    key: "HealthInsuranceContributionsOverview",
                    iconPath: healthInsuranceContributionsOverviewSvg,
                    title: "Menu.HealthInsuranceContributionsOverview",
                    url: HEALTH_INSURANCE_CONTRIBUTIONS_OVERVIEW_ROUTE,
                    permission: [UserPermissionCodes.Reports, UserPermissionCodes.PrPayroll],
                    isVisible: isPayrollEnabledOnCurrentCompany
                },
                {
                    key: "OverviewOfTaxCreditsAndTaxAllowances",
                    iconPath: taxCreditsAndTaxAllowancesOverviewSvg,
                    title: "Menu.OverviewOfTaxCreditsAndTaxAllowances",
                    url: TAX_CREDITS_AND_ALLOWANCES_OVERVIEW_ROUTE,
                    permission: [UserPermissionCodes.Reports, UserPermissionCodes.PrPayroll],
                    isVisible: isPayrollEnabledOnCurrentCompany
                },
                {
                    key: "SocialSecurityContributionsOverview",
                    iconPath: socialContributionOverviewSvg,
                    title: "Menu.SocialSecurityContributionsOverview",
                    url: SOCIAL_SECURITY_CONTRIBUTIONS_OVERVIEW_ROUTE,
                    permission: [UserPermissionCodes.Reports, UserPermissionCodes.PrPayroll],
                    isVisible: isPayrollEnabledOnCurrentCompany
                },
                {
                    key: "VacationAndSickDaysOverview",
                    iconPath: vacationAndSickDaysOverviewSvg,
                    title: "Menu.VacationAndSickDaysOverview",
                    url: VACATION_AND_SICK_DAYS_OVERVIEW_ROUTE,
                    permission: [UserPermissionCodes.Reports, UserPermissionCodes.PrPayroll],
                    isVisible: isPayrollEnabledOnCurrentCompany
                },
                {
                    key: "AdvanceAndWithholdingTax",
                    iconPath: advanceAndWithholdingTaxSvg,
                    title: "Menu.AdvanceAndWithholdingTax",
                    url: ADVANCE_AND_WITHHOLDING_TAX_ROUTE,
                    permission: [UserPermissionCodes.Reports, UserPermissionCodes.PrPayroll],
                    isVisible: isPayrollEnabledOnCurrentCompany
                }
            ]
        },
        {
            key: "Audit",
            title: "Menu.Audit",
            url: ROUTE_AUDIT,
            items: [
                {
                    key: "Messages",
                    iconPath: messagesSvg,
                    title: "Menu.Messages",
                    url: ROUTE_MESSAGES,
                    permission: UserPermissionCodes.Audit
                }, {
                    key: "DeleteLog",
                    iconPath: auditSvg,
                    title: "Menu.DeleteLog",
                    url: ROUTE_DELETE_LOG,
                    permission: UserPermissionCodes.Audit
                }, {
                    key: "BackgroundJob",
                    iconPath: backgroundJobSvg,
                    title: "Menu.BackgroundJob",
                    url: ROUTE_BACKGROUND_JOB
                }

            ]
        },
        {
            key: "Payroll",
            title: "Menu.Payroll",
            url: ROUTE_PAYROLL,
            items: [
                {
                    key: "Attendance",
                    title: "Menu.Attendance",
                    url: ROUTE_ATTENDANCE,
                    iconPath: attendanceSvg,
                    permission: UserPermissionCodes.PrPayroll
                },
                {
                    key: "SocialEvents",
                    iconPath: socialEventsSvg,
                    title: "Menu.SocialEvents",
                    url: ROUTE_SOCIAL_EVENTS,
                    permission: UserPermissionCodes.PrPayroll
                },
                {
                    key: "Submissions",
                    iconPath: prSubmissionsSvg,
                    title: "Menu.Submissions",
                    url: ROUTE_SUBMISSIONS,
                    permission: UserPermissionCodes.PrPayroll
                },
                {
                    key: "Payslip",
                    iconPath: payslipSvg,
                    title: "Menu.Payslip",
                    url: ROUTE_PAYSLIP,
                    permission: UserPermissionCodes.PrPayroll
                },
                {
                    key: "Employees",
                    title: "Menu.Employees",
                    url: ROUTE_EMPLOYEES,
                    iconPath: employeesSvg,
                    permission: UserPermissionCodes.PrPayroll
                },
                // {
                //     key: "EmploymentTemplates",
                //     title: "Menu.EmploymentTemplates",
                //     url: ROUTE_EMPLOYMENT_TEMPLATE,
                //     iconPath: employmentTemplatesSvg,
                //     permission: UserPermissionCodes.PrPayroll
                // },
                {
                    key: "Employment",
                    title: "Menu.Employments",
                    iconPath: employmentSvg,
                    url: ROUTE_EMPLOYMENT,
                    permission: UserPermissionCodes.PrPayroll
                },
                {
                    key: "TaxpayerDeclarations",
                    iconPath: taxPayerDeclarationSvg,
                    title: "Menu.TaxpayerDeclarations",
                    url: ROUTE_TAXPAYER_DECLARATIONS,
                    permission: UserPermissionCodes.PrPayroll
                },
                {
                    key: "SalaryAdvances",
                    title: "Menu.SalaryAdvances",
                    url: ROUTE_SALARY_ADVANCES,
                    iconPath: salaryAdvancesSvg,
                    permission: UserPermissionCodes.PrPayroll
                },
                {
                    key: "SalaryComponents",
                    title: "Menu.SalaryComponents",
                    url: ROUTE_SALARY_COMPONENTS,
                    iconPath: salaryComponentsSvg,
                    permission: UserPermissionCodes.PrPayroll
                },
                {
                    key: "ExtraSalaries",
                    title: "Menu.ExtraSalaries",
                    url: ROUTE_EXTRA_SALARIES,
                    iconPath: extraSalariesSvg,
                    permission: UserPermissionCodes.PrPayroll
                },
                {
                    key: "Deductions",
                    iconPath: deductionsSvg,
                    title: "Menu.Deductions",
                    url: ROUTE_DEDUCTIONS,
                    permission: UserPermissionCodes.PrPayroll
                },
                {
                    key: "Benefits",
                    iconPath: benefitsSvg,
                    title: "Menu.Benefits",
                    url: ROUTE_BENEFITS,
                    permission: UserPermissionCodes.PrPayroll
                },
                {
                    key: "WorkingPatterns",
                    title: "Menu.WorkingPatterns",
                    url: ROUTE_WORKING_PATTERNS,
                    iconPath: workingPatternsSvg,
                    permission: UserPermissionCodes.PrPayroll
                },
                // {
                //     key: "ELDP",
                //     iconPath: eldpSvg,
                //     title: "Menu.Eldp",
                //     url: ROUTE_ELDP,
                //     permission: UserPermissionCodes.PrPayroll
                // },
                {
                    key: "AnnualTaxSettlementRequests",
                    iconPath: annualTaxSettlementRequestsSvg,
                    title: "Menu.AnnualTaxSettlementRequests",
                    url: ROUTE_ANNUAL_TAX_SETTLEMENT_REQUEST,
                    permission: UserPermissionCodes.PrPayroll
                },
                {
                    key: "AnnualTaxSettlement",
                    iconPath: annualTaxSettlementSvg,
                    title: "Menu.AnnualTaxSettlement",
                    url: ROUTE_ANNUAL_TAX_SETTLEMENT,
                    permission: UserPermissionCodes.PrPayroll
                },
                {
                    key: "PayrollYearOperations",
                    iconPath: prYearlyOperationsSvg,
                    title: "Menu.PayrollYearOperations",
                    url: ROUTE_PAYROLL_YEAR_OPERATIONS,
                    permission: UserPermissionCodes.PrPayroll
                }
            ],
            isVisible: isPayrollEnabledOnCurrentCompany
        }
    ];
};

// there are some pages/sections, which are not directly displayed in menu, however we want to have them
// organized in structure, so things like breadcrumbs works automagically
export const NonMenuNavigations: IMenuDefGroup[] = [
    {
        key: "Customer",
        title: "Menu.Customer",
        url: ROUTE_HOME,
        items: [
            {
                key: "Chats",
                title: "Menu.Chats",
                url: ROUTE_CHATS
            },
            {
                key: "InboxApproval",
                title: "Menu.InboxApproval",
                url: ROUTE_INBOX_CUSTOMER_APPROVAL
            },
            {
                key: "InboxSendToAccountant",
                title: "Menu.InboxSendToAccountant",
                url: ROUTE_INBOX_SEND_TO_ACCOUNTANT
            },
            {
                key: "CustomerPortalPayment",
                title: "Menu.CustomerPortalPayment",
                url: ROUTE_CUSTOMER_PORTAL_PAYMENT
            },
            {
                key: "EmployeePayslip",
                title: "Menu.EmployeePayslip",
                url: ROUTE_EMPLOYEE_PAYSLIP
            },
            {
                key: "PayslipOverview",
                title: "Menu.PayslipOverview",
                url: ROUTE_PAYSLIP_OVERVIEW
            },
            {
                key: "SocialRequests",
                title: "Menu.SocialRequests",
                url: ROUTE_SOCIAL_REQUESTS
            }
        ]
    }
];

const notYetPaidForDisabled: IMenuDefItem["isDisabledFn"] = (context: IAppContext): IDisabledMenuItem => {
    const subscription = context.getData().subscription;
    const isDisabled = isNotYetPayedForSubscription(subscription);

    if (!isDisabled) {
        return null;
    }

    return {
        reason: i18next.t("Common:OrganizationSettingsMenu.WithoutSubscriptionDisabledMenuTile")
    };
};

const notYedPaidForOrFreeDisabled: IMenuDefItem["isDisabledFn"] = (context: IAppContext): IDisabledMenuItem => {
    const subscription = context.getData().subscription;
    const isDisabled = isNotYetPayedForSubscription(subscription) || isFreeSubscription(subscription);

    if (!isDisabled) {
        return null;
    }

    return {
        reason: i18next.t("Common:OrganizationSettingsMenu.WithoutSubscriptionDisabledMenuTile")
    };
};

const isDisabledForDemoTenant: IMenuDefItem["isDisabledFn"] = (context: IAppContext): IDisabledMenuItem => {
    if (isDemoTenant(context)) {
        return {
            reason: i18next.t("Common:OrganizationSettingsMenu.DemoTenantDisabledMenuTile")
        };
    }
    return null;
};

/**
 * Note: order matters, first function is checked first
 * @param params
 */
function merge(...params: IMenuDefItem["isDisabledFn"][]): IMenuDefItem["isDisabledFn"] {
    return (context: IAppContext): IDisabledMenuItem => {
        for (const param of params) {
            const result = param(context);
            if (result) {
                return result;
            }
        }
        return null;
    };
}

export const OrganizationSettingsMenuDefinition: IMenuDefGroup[] = [
    {
        key: "UsersMenu",
        title: "OrganizationSettingsMenu.Users",
        url: ROUTE_MENU_USERS,
        items: [
            {
                key: "Users",
                iconPath: usersSvg,
                title: "OrganizationSettingsMenu.UsersSettings",
                url: ROUTE_USERS,
                permission: GeneralPermissionCode.OrganizationManagement
            },
            {
                key: "CompanyRoles",
                iconPath: companyRolesSvg,
                title: "OrganizationSettingsMenu.CompanyRoles",
                url: ROUTE_COMPANY_ROLES,
                permission: GeneralPermissionCode.OrganizationManagement
            },
            {
                key: "GeneralRoles",
                iconPath: generalRolesSvg,
                title: "OrganizationSettingsMenu.GeneralRoles",
                url: ROUTE_SYSTEM_ROLES,
                permission: GeneralPermissionCode.OrganizationManagement
            }
        ]
    },
    {
        key: "Subscriptions",
        title: "OrganizationSettingsMenu.Subscriptions",
        url: ROUTE_MENU_SUBSCRIPTION,
        items: [
            {
                key: "Plan",
                iconPath: subscriptionPlanSvg,
                title: "OrganizationSettingsMenu.Plan",
                url: ROUTE_SUBSCRIPTIONS_PLAN,
                permission: GeneralPermissionCode.OrganizationManagement,
                isDisabledFn: isDisabledForDemoTenant
            },
            {
                key: "Invoicing",
                iconPath: subscriptionInvoicingSvg,
                title: "OrganizationSettingsMenu.Invoicing",
                url: ROUTE_SUBSCRIPTIONS_INVOICING,
                permission: GeneralPermissionCode.OrganizationManagement,
                isDisabledFn: merge(isDisabledForDemoTenant, notYedPaidForOrFreeDisabled)
            },
            {
                key: "Payments",
                iconPath: subscriptionPaymentsSvg,
                title: "OrganizationSettingsMenu.Payments",
                url: ROUTE_SUBSCRIPTIONS_PAYMENTS,
                permission: GeneralPermissionCode.OrganizationManagement,
                isDisabledFn: merge(isDisabledForDemoTenant, notYetPaidForDisabled)
            },
            {
                key: "Extensions",
                iconPath: subscriptionExtensionsSvg,
                title: "OrganizationSettingsMenu.Extensions",
                url: ROUTE_SUBSCRIPTIONS_EXTENSIONS,
                permission: GeneralPermissionCode.OrganizationManagement,
                isDisabledFn: merge(isDisabledForDemoTenant, notYedPaidForOrFreeDisabled)
            }
        ]
    },
    {
        key: "SharedAgendaSettings",
        title: "OrganizationSettingsMenu.SharedAgendaSettings",
        url: ROUTE_MENU_COMMON_SETTINGS,
        items: [
            {
                key: "AccountAssignment",
                iconPath: accountAssignmentSvg,
                title: "OrganizationSettingsMenu.AccountAssignment",
                url: ROUTE_ACCOUNT_ASSIGNMENT,
                permission: GeneralPermissionCode.TemplateManagement
            },
            {
                key: "Vat",
                iconPath: vatRulesSvg,
                title: "OrganizationSettingsMenu.Vats",
                url: ROUTE_VAT,
                permission: GeneralPermissionCode.TemplateManagement
            },
            {
                key: "Templates",
                iconPath: coaTemplatesSvg,
                title: "OrganizationSettingsMenu.Templates",
                url: ROUTE_CHARTS_OF_ACCOUNTS_TEMPLATES,
                permission: GeneralPermissionCode.TemplateManagement
            },
            {
                key: "ItemTemplates",
                iconPath: itemTemplatesSvg,
                title: "OrganizationSettingsMenu.ItemTemplates",
                url: ROUTE_DOCUMENT_ITEM_TEMPLATES,
                permission: GeneralPermissionCode.TemplateManagement
            },
            {
                key: "RetirementSavingsCompanies",
                title: "Menu.RetirementCompanies",
                iconPath: retirementSavingsSvg,
                url: ROUTE_RETIREMENT_SAVINGS_COMPANIES,
                permission: GeneralPermissionCode.TemplateManagement,
                isVisible: isPayrollEnabledOnTenantLevel

            }
        ]
    }
];

export const AddMenuDefinition: IMenuDefGroup[] = [
    {
        title: "AddMenu.ReceivedDocuments",
        key: "AddMenu.ReceivedDocuments",
        items: [
            {
                key: "ReceivedInvoice",
                title: "AddMenu.Invoice",
                url: `${ROUTE_INVOICE_RECEIVED}/new`,
                permission: UserPermissionCodes.InvoicesReceived
            },
            {
                key: "ReceivedProforma",
                title: "AddMenu.Proforma",
                url: `${ROUTE_PROFORMA_INVOICE_RECEIVED}/new?type=${ProformaType.Proforma}`,
                permission: UserPermissionCodes.InvoicesReceived
            },
            {
                key: "ReceivedDDOPP",
                title: "AddMenu.DDOPP",
                url: `${ROUTE_PROFORMA_INVOICE_RECEIVED}/new?type=${ProformaType.DDOP}`,
                permission: UserPermissionCodes.InvoicesReceived,
                isVisible: (args: IGetValueArgs): boolean => {
                    return hasEverBeenVatRegisteredCompany(args.context);
                }
            },
            {
                key: "ReceivedCorrectiveDoc",
                title: "AddMenu.CorrectiveDoc",
                url: `${ROUTE_CORRECTIVE_INVOICE_RECEIVED}/new`,
                permission: UserPermissionCodes.InternalDocuments
            }
        ]
    },
    {
        title: "AddMenu.IssuedDocuments",
        key: "AddMenu.IssuedDocuments",
        items: [
            {
                key: "IssuedInvoice",
                title: "AddMenu.Invoice",
                url: `${ROUTE_INVOICE_ISSUED}/new`,
                permission: UserPermissionCodes.InvoicesIssued
            },
            {
                key: "IssuedProforma",
                title: "AddMenu.Proforma",
                url: `${ROUTE_PROFORMA_INVOICE_ISSUED}/new?type=${ProformaType.Proforma}`,
                permission: UserPermissionCodes.InvoicesIssued
            },
            {
                key: "IssuedDDOPP",
                title: "AddMenu.DDOPP",
                url: `${ROUTE_PROFORMA_INVOICE_ISSUED}/new?type=${ProformaType.DDOP}`,
                permission: UserPermissionCodes.InvoicesIssued,
                isVisible: (args: IGetValueArgs): boolean => {
                    return hasEverBeenVatRegisteredCompany(args.context);
                }
            },
            {
                key: "IssuedCorrectiveDoc",
                title: "AddMenu.CorrectiveDoc",
                url: `${ROUTE_CORRECTIVE_INVOICE_ISSUED}/new`,
                permission: UserPermissionCodes.InternalDocuments
            }
        ]
    },
    {
        title: "AddMenu.OtherDocuments",
        key: "AddMenu.OtherDocuments",
        items: [
            {
                key: "OtherLiability",
                title: "AddMenu.OtherLiability",
                url: `${ROUTE_OTHER_LIABILITY}/new`,
                permission: UserPermissionCodes.InvoicesReceived
            },
            {
                key: "OtherLiabilityEmployee",
                title: "AddMenu.OtherLiabilityEmployee",
                url: `${ROUTE_OTHER_LIABILITY}/new?type=${DocumentBusinessPartnerTypeCode.Employee}`,
                permission: UserPermissionCodes.InvoicesReceived,
                isVisible: (args): boolean =>
                    hasPayrollEnabled(args.context.getCompany())
            },
            {
                key: "OtherReceivable",
                title: "AddMenu.OtherReceivable",
                url: `${ROUTE_OTHER_RECEIVABLE}/new`,
                permission: UserPermissionCodes.InvoicesIssued
            },
            {
                key: "OtherReceivableEmployee",
                title: "AddMenu.OtherReceivableEmployee",
                url: `${ROUTE_OTHER_RECEIVABLE}/new?type=${DocumentBusinessPartnerTypeCode.Employee}`,
                permission: UserPermissionCodes.InvoicesIssued,
                isVisible: (args): boolean =>
                    hasPayrollEnabled(args.context.getCompany())
            },
            {
                key: "InternalDoc",
                title: "AddMenu.InternalDoc",
                url: `${ROUTE_INTERNAL_DOCUMENT}/new`,
                permission: UserPermissionCodes.InternalDocuments
            }
        ]
    }
];

export const getAllMenuGroups = (context: IAppContext): IMenuDefGroup[] => {
    return [
        ...SettingsDefinition,
        ...getMainMenuDefinition(context),
        ...OrganizationSettingsMenuDefinition
    ];
};

export const findItemAndGroup = (definition: IMenuDefGroup[], correctPath: string): IMenuSelected => {
    let selectedView;
    const selectedGroup = definition.find((group: IMenuDefGroup) => {
        selectedView = group.items.find((item) => item.url.toLowerCase() === correctPath?.toLowerCase());
        return selectedView;
    });
    return selectedGroup && { group: selectedGroup, item: selectedView };
};

export const filterMenu = (menuGroups: IMenuDefGroup[], context: IAppContext, permissionContext: IPermissionContext): IMenuDefGroup[] => {
    const filteredMenu = cloneDeep(menuGroups);
    const accountingType = getCompanyAccountingCode(context);
    const lookForPermission = (permission: string) => {
        return permissionContext.hasCompanyPermissions(permission as CompanyPermissionCode) || permissionContext.generalPermissions.has(permission as GeneralPermissionCode);
    };

    for (const menuGroup of filteredMenu) {
        menuGroup.items = menuGroup.items.filter(item => {
            if (isDefined(item.isVisible) && !getValue(item.isVisible, { context })) {
                return false;
            }

            if (item.accountingTypes && !item.accountingTypes.includes(accountingType)) {
                return false;
            }

            if (!item.permission) {
                return true;
            }

            if (typeof item.permission === "string") {
                return lookForPermission(item.permission);
            } else {
                return item.permission.some(p => lookForPermission(p));
            }
        });
    }
    return filteredMenu;
};
